/**
 * myDayFocus.js
 *
 * Pure helpers of the prioritised part ("Fókusz") of the "Mai napom" briefing: the safe shape of
 * `GET /crm/follow-ups/focus`, one unified ranking over everything the user has to deal with today
 * (calendar events, fixed to-dos, work items, mail, projects, leads, dormant customers) and the payload of the
 * AI day plan. No React / storage / network imports — Jest-safe.
 *
 * Ranking rule (what the user asked for): fixed appointments come FIRST — today's calendar events and the
 * to-dos with a fixed time (overdue ones before today's, then chronological). Everything else follows by
 * a 0-100 score (the server's own score for projects / leads / customers).
 *
 * @module helpers/myDayFocus
 */

import {entityOf, plainText} from './salesFollowUpApi';

/** Severity vocabulary, most urgent first. */
export const FOCUS_SEVERITIES = Object.freeze(['critical', 'high', 'medium', 'low']);

/** Rows shown in the "Most ezzel foglalkozz" block and sent to the AI. */
export const TOP_LIMIT = 6;
export const PLAN_LIMIT = 25;

const HOUR_MS = 60 * 60 * 1000;
const asArray = (v) => (Array.isArray(v) ? v.filter((x) => x && typeof x === 'object') : []);
const str = (v) => (typeof v === 'string' ? v : (typeof v === 'number' ? String(v) : ''));
const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);
const toMs = (v) => {
    const t = v ? new Date(v).getTime() : NaN;

    return Number.isNaN(t) ? null : t;
};

/**
 * Severity of a 0-100 score (same buckets as the backend).
 * @param {number} score score. @returns {'critical'|'high'|'medium'|'low'} severity.
 */
export const severityOf = (score) => {
    if (score >= 85) {
        return 'critical';
    }

    if (score >= 65) {
        return 'high';
    }

    return score >= 40 ? 'medium' : 'low';
};

const normalizeReasons = (list) => asArray(list)
    .map((r) => ({code: str(r.code), params: r.params && typeof r.params === 'object' ? r.params : {}}))
    .filter((r) => r.code);

const normalizeFocusItem = (raw) => {
    if (!raw || typeof raw !== 'object' || !str(raw.key)) {
        return null;
    }

    const score = Math.max(0, Math.min(100, num(raw.score)));

    return {
        key: str(raw.key),
        kind: str(raw.kind),
        id: str(raw.id),
        title: str(raw.title),
        subtitle: str(raw.subtitle),
        dueAt: raw.dueAt || null,
        score,
        severity: FOCUS_SEVERITIES.includes(raw.severity) ? raw.severity : severityOf(score),
        reasons: normalizeReasons(raw.reasons),
        meta: raw.meta && typeof raw.meta === 'object' ? raw.meta : {},
    };
};

/**
 * Safe focus block: every list is an array of normalized items, totals are numbers.
 * An old server (404 / 405) simply yields the empty block.
 * @param {object} raw GET /crm/follow-ups/focus body (may be null).
 * @returns {{projects: object[], leads: object[], customers: object[], totals: {projects: number, leads: number, customers: number}, available: boolean}} focus.
 */
export const normalizeFocus = (raw) => {
    const r = raw && typeof raw === 'object' ? raw : {};
    const list = (v) => asArray(v).map(normalizeFocusItem).filter(Boolean);
    const t = r.totals || {};

    return {
        projects: list(r.projects),
        leads: list(r.leads),
        customers: list(r.customers),
        totals: {projects: num(t.projects), leads: num(t.leads), customers: num(t.customers)},
        available: !!raw && typeof raw === 'object',
    };
};

/**
 * Localised one-line text of a reason (`myday_reason_<code>`; unknown codes fall back to the code itself).
 * @param {Function} t i18n translate. @param {{code: string, params: object}} reason reason.
 * @returns {string} text.
 */
export const reasonText = (t, reason) => {
    const code = str(reason && reason.code);

    if (!code) {
        return '';
    }

    const params = {...(reason.params || {})};

    if (params.status) {
        params.status = t(`lead_status_${String(params.status).replace(/-/g, '_')}`, {defaultValue: String(params.status)});
    }

    return t(`myday_reason_${code.replace(/-/g, '_')}`, {...params, defaultValue: code});
};

const followUpTitle = (f) => plainText(entityOf(f).name) || plainText(f.title) || '—';

/**
 * One unified, ranked list over the agenda and the focus block.
 * @param {object} params params.
 * @param {object} params.agenda normalized agenda ({@link module:helpers/salesFollowUpApi.normalizeAgenda}).
 * @param {object} [params.focus] normalized focus ({@link normalizeFocus}).
 * @param {number} [params.now] epoch ms.
 * @returns {object[]} rows `{key, source, kind, title, subtitle, dueAt, severity, score, fixed, reasons, ref}` — fixed
 *   appointments first, then by score. `source`: event | followUp | work | project | lead | customer.
 */
export const rankPriorities = ({agenda, focus, now = Date.now()}) => {
    const a = agenda || {};
    const rows = [];
    const followUpsOn = !(a.access && a.access.followUps === false);

    asArray(a.calendarEvents).forEach((ev) => {
        const start = toMs(ev.start);
        const end = toMs(ev.end);

        // Finished events are not a priority any more.
        if (!ev.allDay && (end ?? start) != null && (end ?? start) < now) {
            return;
        }

        const soon = start != null && start - now <= 2 * HOUR_MS && start - now > -HOUR_MS;

        rows.push({
            key: `event:${str(ev._id || ev.id)}`,
            source: 'event',
            kind: 'event',
            title: plainText(ev.title) || '—',
            subtitle: plainText(ev.location) || '',
            dueAt: ev.start || null,
            allDay: !!ev.allDay,
            severity: soon ? 'critical' : 'high',
            score: soon ? 96 : 90,
            fixed: true,
            reasons: soon ? [{code: 'event-soon', params: {}}] : [],
            ref: ev,
        });
    });

    if (followUpsOn) {
        [...asArray(a.overdue).map((f) => ({f, overdue: true})), ...asArray(a.today).map((f) => ({f, overdue: false}))].forEach(({f, overdue}) => {
            const due = toMs(f.dueAt);
            const late = overdue || (due != null && due < now);

            rows.push({
                key: `followUp:${str(f._id || f.id)}`,
                source: 'followUp',
                kind: str(f.type) || 'other',
                title: followUpTitle(f),
                subtitle: plainText(entityOf(f).company),
                dueAt: f.dueAt || null,
                severity: late ? 'critical' : 'high',
                score: late ? 88 : 85,
                fixed: true,
                reasons: late ? [{code: 'overdue', params: {}}] : [],
                ref: f,
            });
        });
    }

    const work = a.workItems || {};

    [...asArray(work.overdue).map((w) => ({w, overdue: true})), ...asArray(work.today).map((w) => ({w, overdue: false}))].forEach(({w, overdue}) => {
        const isMail = w.kind === 'mail';
        const mail = isMail && w.mail ? w.mail : null;
        const prio = String(w.priority || '').toLowerCase();
        let score = overdue || w.isOverdue ? 68 : 58;
        const reasons = [];

        if (prio === 'urgent' || prio === 'critical') {
            score += 14;
            reasons.push({code: 'priority-high', params: {}});
        } else if (prio === 'high') {
            score += 8;
            reasons.push({code: 'priority-high', params: {}});
        }

        if (mail) {
            score = mail.important ? 66 : 50;
            score += Math.min(10, Math.floor(num(mail.hoursWaiting) / 12));

            if (mail.unanswered) {
                reasons.push({code: 'mail-unanswered', params: {hours: Math.round(num(mail.hoursWaiting))}});
            }

            if (mail.important) {
                reasons.push({code: 'mail-important', params: {}});
            }
        } else if (overdue || w.isOverdue) {
            reasons.push({code: 'overdue', params: {}});
        }

        score = Math.min(84, score);
        rows.push({
            key: `work:${str(w.kind)}:${str(w.id)}`,
            source: 'work',
            kind: str(w.kind),
            title: plainText(w.title),
            subtitle: plainText(w.subtitle) || plainText(w.entity && w.entity.name),
            dueAt: w.dueAt || null,
            severity: severityOf(score),
            score,
            fixed: false,
            reasons,
            ref: w,
        });
    });

    const f = focus || {};

    [...asArray(f.projects), ...asArray(f.leads), ...asArray(f.customers)].forEach((item) => {
        rows.push({
            key: item.key,
            source: item.kind,
            kind: item.kind,
            title: item.title,
            subtitle: item.subtitle,
            dueAt: item.dueAt,
            severity: item.severity,
            score: item.score,
            fixed: false,
            reasons: item.reasons,
            ref: item,
        });
    });

    return rows.sort((x, y) => {
        if (x.fixed !== y.fixed) {
            return x.fixed ? -1 : 1;
        }

        if (x.fixed) {
            // Overdue fixed items first, then chronological.
            const dx = toMs(x.dueAt) ?? Infinity;
            const dy = toMs(y.dueAt) ?? Infinity;

            return dx - dy;
        }

        return y.score - x.score || (toMs(x.dueAt) ?? Infinity) - (toMs(y.dueAt) ?? Infinity);
    });
};

/**
 * Payload rows of the AI day plan (localised reason texts, at most {@link PLAN_LIMIT}).
 * @param {object[]} ranked rows of {@link rankPriorities}. @param {Function} t i18n translate.
 * @returns {object[]} `{key, kind, title, subtitle, dueAt, severity, reasons}`.
 */
export const toPlanItems = (ranked, t) => asArray(ranked).slice(0, PLAN_LIMIT).map((row) => ({
    key: row.key,
    kind: row.source === 'work' ? row.kind : row.source,
    title: String(row.title || '').slice(0, 160),
    subtitle: String(row.subtitle || '').slice(0, 160),
    dueAt: row.dueAt ? new Date(row.dueAt).toISOString() : null,
    severity: row.severity,
    reasons: row.reasons.map((r) => reasonText(t, r)).filter(Boolean),
}));

/**
 * Safe AI plan block (`POST /crm/follow-ups/focus/analyze`).
 * @param {object} raw response body. @returns {{headline: string, summary: string, plan: object[], tips: string[], cached: boolean}} plan.
 */
export const normalizePlan = (raw) => {
    const r = raw && typeof raw === 'object' ? raw : {};

    return {
        headline: str(r.headline),
        summary: str(r.summary),
        plan: asArray(r.plan).map((p) => ({key: str(p.key), action: str(p.action), why: str(p.why)})).filter((p) => p.key),
        tips: (Array.isArray(r.tips) ? r.tips : []).map(str).filter(Boolean),
        cached: !!r.cached,
    };
};

/**
 * Safe AI mail reply draft (`POST /crm/follow-ups/focus/reply-draft`).
 * @param {object} raw response body. @returns {{to: {name: string, email: string}, subject: string, body: string, bodyHtml: string}|null} draft or null.
 */
export const normalizeDraft = (raw) => {
    const r = raw && typeof raw === 'object' ? raw : {};
    const body = str(r.body);

    if (!body) {
        return null;
    }

    return {
        to: {name: str(r.to && r.to.name), email: str(r.to && r.to.email)},
        subject: str(r.subject),
        body,
        bodyHtml: str(r.bodyHtml),
    };
};

/**
 * Route params that open the mail composer with a reviewed draft (nothing is sent automatically).
 * @param {{to: {name: string, email: string}, subject: string, bodyHtml: string}} draft normalized draft.
 * @returns {{mode: string, prefillTo: object[], prefillSubject: string, prefillBody: string}} `MailComposeScreen` params.
 */
export const draftComposeParams = (draft) => ({
    mode: 'new',
    prefillTo: draft.to.email ? [{name: draft.to.name || undefined, email: draft.to.email}] : [],
    prefillSubject: draft.subject,
    prefillBody: draft.bodyHtml,
});
